import * as HttpService from 'services/http.service';
import { GET_SEARCH_SUGGESTIONS } from 'services/url.service';

const defaultPriceRange = Object.freeze({
  max_price: { value: 0 },
  min_price: { value: 0 }
});

const defaultPriceAggregation = Object.freeze({
  aggregations: { price: defaultPriceRange }
});

const defaultPriceRangeBySlug = Object.freeze({
  entity: defaultPriceAggregation
});

const deepClonePreserveUndefined = (input) => {
  if (Array.isArray(input)) {
    return input.map(deepClonePreserveUndefined);
  }
  if (input && typeof input === 'object') {
    const out = {};
    for (const key of Object.keys(input)) {
      out[key] = deepClonePreserveUndefined(input[key]);
    }
    return out;
  }
  return input;
};

const isValidValue = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
};

const hasValidTermValue = (term) => {
  if (!term || typeof term !== 'object') return false;

  const entryValues = Object.values(term);
  if (entryValues.length === 0) return false;

  return entryValues.every((value) => {
    if (!isValidValue(value)) return false;

    const isValueObject = typeof value === 'object';
    if (isValueObject) {
      if (!isValidValue(value.value)) return false;
    }

    return true;
  });
};

const getCleanedFields = (fieldsInput) => {
  const fields = Array.isArray(fieldsInput) ? fieldsInput : [fieldsInput];

  const normalizedFieldsToArrayAndCleanUpValues = fields
    .filter((f) => typeof f === 'string')
    .map((f) => f.trim())
    .filter((f) => f.length > 0);

  const deDuplicated = [...new Set(normalizedFieldsToArrayAndCleanUpValues)];
  return deDuplicated;
};

const hasValidMultiMatchQuery = (multiMatch) => {
  if (!multiMatch || typeof multiMatch !== 'object') return false;

  const { query } = multiMatch;
  if (typeof query !== 'string' || query.trim() === '') return false;

  const deDuplicatedFields = getCleanedFields(multiMatch.fields);
  if (deDuplicatedFields.length === 0) return false;

  return true;
};

const clauseValidationRules = [
  [
    (clause) => clause !== undefined && clause !== null,
    'Clause must be defined'
  ],
  [
    (clause) => typeof clause === 'object' && !Array.isArray(clause),
    'Clause must be an object'
  ],
  [(clause) => Object.keys(clause).length > 0, 'Clause must not be empty'],
  [(clause) => !clause.match_all, 'Clause must not contain match_all'],
  [
    (clause) => !('term' in clause) || hasValidTermValue(clause.term),
    'Invalid term clause'
  ],
  [
    (clause) =>
      !('multi_match' in clause) || hasValidMultiMatchQuery(clause.multi_match),
    'Invalid multi_match clause'
  ]
];

const isValidQueryClause = (clause) =>
  clauseValidationRules.every(([rule]) => rule(clause));

export const processSearchQuery = (plainQueryObject) => {
  const query = deepClonePreserveUndefined(plainQueryObject || {});

  const noQueryObject = !query.query || typeof query.query !== 'object';
  if (noQueryObject) {
    query.query = { match_none: {} };
    return query;
  }

  const boolClause = query.query.bool;

  const shouldExistsButIsNotAnArray =
    boolClause?.should && !Array.isArray(boolClause.should);
  if (shouldExistsButIsNotAnArray) {
    query.query = { match_none: {} };
    return query;
  }

  if (Array.isArray(boolClause?.should)) {
    const filtered = boolClause.should.filter(isValidQueryClause);
    if (filtered.length === 0) {
      query.query = { match_none: {} };
      return query;
    }

    query.query.bool = {
      ...query.query.bool,
      should: filtered,
      minimum_should_match: query.query.bool.minimum_should_match || 1
    };
  }

  const isQueryStillEmptyAfterCleanup = Object.keys(query.query).length === 0;
  if (isQueryStillEmptyAfterCleanup) {
    query.query = { match_none: {} };
  }

  return query;
};

export const fetchPriceRangeBySlug = async ({ apiFunction, key, slug }) => {
  try {
    const {
      entity: {
        aggregations: { price = defaultPriceRange } = defaultPriceAggregation
      } = defaultPriceRangeBySlug
    } = await apiFunction({ key, value: slug });

    return price;
  } catch (error) {
    console.error('Error fetching price range:', error);
    return defaultPriceRange;
  }
};

export const getSuggestions = (text) =>
  HttpService.getWithoutAuth(GET_SEARCH_SUGGESTIONS(text));
